import React, { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";

import Box from "@material-ui/core/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import WarningIcon from "@material-ui/icons/Warning";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

import Header from "../Header/Header";
import RoutingContainer from "../RoutingContainer/RoutingContainer";
import ErrorBoundary from "./ErrorBoundary";
import SysOutage, { MSG_SYSTEM_OUTAGE } from "../Alert/Sysinfo/SysOutage";
import GenericIssues from "../Alert/GenericIssues/GenericIssues";

import Footer from "../Footer/Footer";
import BackToTop from "../UI/BackToTop/BackToTop";
import { outageWarning, allGenericIssue } from "../../utils/LDFFUtils";

import PageAlert, {
  BROWSER_DOWNLOAD_SIZE_INFO,
  GLOBUS_DOWNLOAD_SIZE_INFO,
  TAPE_FILE_INFO,
  RESTORE_WILL_OVER_USER_QUOTA,
  RESTORE_OVER_USER_QUOTA,
  RESTORE_NEAR_USER_QUOTA,
  RESTORE_DISK_SPACE,
} from "../Alert/PageAlert/PageAlert";

const AppContainer = (props) => {
  const history = useHistory();
  const { honeycomb, ldClient, currentUser } = props;

  const [oversize, setOversize] = useState(null);
  const [tapedata, setTapedata] = useState(null);
  const [restore, setRestoreMessageId] = useState(null);
  const [topRect, setTopRect] = useState(null);
  const [mimicInfo, setMimicInfo] = useState(null);

  const topRef = useRef(null);
  const headerCartRef = useRef(null); // for shopping cart in header

  /* istanbul ignore next */
  useEffect(
    () =>
      history.listen((location) => {
        if (location.pathname === "/") {
          setOversize(null);
          setTapedata(null);
          setRestoreMessageId(null);
        }
        honeycomb.sendRoutingSpan(location.pathname);
      }),
    [history, honeycomb]
  );

  const handleResize = () => {
    if (topRef?.current) {
      const rect = topRef.current.getBoundingClientRect();
      setTopRect(rect);
    }
  };

  /* istanbul ignore next */
  useEffect(() => {
    if (currentUser?.mimic && !currentUser?.mimic.error) {
      const { mimic } = currentUser;
      const info = [
        `${mimic.first_name} ${mimic.last_name}`,
        mimic.email_address.toLowerCase(),
        `Contact ID: ${mimic.contact_id}`,
      ];
      setMimicInfo(info);
    }
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // initail size - windown listener will not catch this
    handleResize();
  }, [topRef.current]);

  const setAlertMessageID = useCallback((mid, show = true) => {
    // mid : message ID
    let updateFunc = null;

    switch (mid) {
      case GLOBUS_DOWNLOAD_SIZE_INFO:
      case BROWSER_DOWNLOAD_SIZE_INFO:
        updateFunc = setOversize;
        break;
      case TAPE_FILE_INFO:
        updateFunc = setTapedata;
        break;
      /* eslint-disable */
      /* istanbul ignore next */
      case RESTORE_WILL_OVER_USER_QUOTA:
      /* istanbul ignore next */
      case RESTORE_DISK_SPACE:
      /* istanbul ignore next */
      case RESTORE_OVER_USER_QUOTA:
      /* istanbul ignore next */
      case RESTORE_NEAR_USER_QUOTA:
        /* eslint-enable */
        updateFunc = setRestoreMessageId;
        break;
      default:
        /* istanbul ignore next */
        break;
    }

    updateFunc(show ? mid : null);
  }, []);

  let sysInfo = null;
  if (ldClient) {
    const warning = outageWarning(ldClient);
    if (warning && warning !== "OFF") {
      sysInfo = (
        <SysOutage
          type={MSG_SYSTEM_OUTAGE}
          text={warning}
          honeycomb={honeycomb}
        />
      );
    }

    /* istanbul ignore next */
    if (!sysInfo) {
      const gIssue = allGenericIssue(ldClient);
      if (gIssue?.length > 0) {
        sysInfo = gIssue.map((d, idx) => {
          const key = `${d.title.toLowerCase().replace(/ /g, "_")}_${idx}`;
          return (
            <GenericIssues
              key={key}
              title={d.title}
              subtitle={d.subtitle}
              message={d.message}
            />
          );
        });
      }
    }
  }

  const [headerCartThreshold, setHeaderCartThreshold] = useState(0); // to be shared with header and downloader for cart tool display
  useEffect(() => {
    if (headerCartRef?.current && headerCartThreshold === 0) {
      const cartRect = headerCartRef.current.getBoundingClientRect();
      setHeaderCartThreshold(cartRect.top + cartRect.height - 14);
    }
  }, [headerCartRef]);

  return (
    <>
      <Box
        data-testid="body-box"
        style={{ minHeight: "calc(100vh - 115px)" }}
        ref={topRef}
        className="AppRoot"
      >
        <Header
          {...props}
          headerCartRef={headerCartRef}
          headerCartThreshold={headerCartThreshold}
        />
        {sysInfo}
        <PageAlert mid={oversize} ldClient={ldClient} />
        <PageAlert mid={tapedata} ldClient={ldClient} />
        <PageAlert mid={restore} ldClient={ldClient} />

        <ErrorBoundary {...props}>
          <RoutingContainer
            topRect={topRect}
            topRef={topRef}
            {...props}
            setAppMessageID={setAlertMessageID}
            headerCartThreshold={headerCartThreshold}
          />
        </ErrorBoundary>
      </Box>

      <Footer {...props} />

      <BackToTop topRef={topRef} honeycomb={honeycomb} />

      {/*  display a sticky and centered banner to top of page  */}
      {mimicInfo && (
        <Box
          style={{
            position: "fixed",
            top: 0,
            zIndex: 10000,
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            style={{
              backgroundColor: "#FFE9BD",
              borderBottom: "2px solid #bbb",
              borderLeft: "2px solid #bbb",
              borderRight: "2px solid #bbb",
              paddingLeft: 40,
              paddingRight: 40,
              paddingBottom: 4,
              borderBottomRightRadius: 10,
              borderBottomLeftRadius: 10,
            }}
          >
            <Grid container>
              <WarningIcon style={{ color: "orange" }} />
              <Typography
                sx={{
                  fontWeight: "bold",
                  paddingLeft: 1,
                  paddingRight: 1,
                  fontSize: 18,
                }}
              >
                Mimicking user:{" "}
              </Typography>

              {mimicInfo.map((d, idx) => {
                return (
                  <React.Fragment key={d.toLowerCase().replace(/ /g, "_")}>
                    <Typography
                      sx={{ fontSize: 18, paddingRight: 1, paddingLeft: 1 }}
                    >
                      {d}
                    </Typography>
                    {idx < mimicInfo.length - 1 && (
                      <FiberManualRecordIcon
                        style={{
                          position: "relative",
                          width: 12,
                          height: 12,
                          top: 8,
                          color: "#444",
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </Grid>
          </Box>
        </Box>
      )}
    </>
  );
};

export default AppContainer;
AppContainer.defaultProps = {
  honeycomb: { sendRoutingSpan: () => {} },
  ldClient: null,
  currentUser: null,
};

AppContainer.propTypes = {
  honeycomb: PropTypes.shape({
    sendRoutingSpan: PropTypes.func,
  }),
  ldClient: PropTypes.shape({
    variation: PropTypes.func,
  }),
  currentUser: PropTypes.shape(),
};
